<script setup>
import { InitViewIDs } from '@src/config';
import { computed, onMounted, ref, watchEffect } from 'vue';

import { useOCGCalculatorStore } from '@/src/store/calculators/OCGCalculatorStore';

import { useViewportStateStore } from '../store/contours/viewport-state-store';
import { useCurrentStudyStore } from '../store/dicom-web/current-study-store';
import { useAppState } from '../store/ocg-app-state';
import OcgCurvesPanel from './analysis/OcgCurvesPanel.vue';
import SeriesThumbnailsOverlay from './SeriesThumbnailsOverlay.vue';
import VtkTwoView from './VtkTwoView.vue';

const currentStudyStore = useCurrentStudyStore();
const viewportStateStore = useViewportStateStore();
const appStateStore = useAppState();
const ocgStore = useOCGCalculatorStore();

const userInteractActiveViewer = computed(() => appStateStore.userActiveViewer);
const generalViewerViewID = ref(InitViewIDs.AnalysisGeneralView);
const isCurvesOpen = ref(false);

const activeViewerID = computed(() => generalViewerViewID.value);

const currentImageIndices = computed(() => {
  if (userInteractActiveViewer.value) {
    return viewportStateStore.getSelectedImageIndices(
      userInteractActiveViewer.value,
    );
  }
  return null;
});

const selfIndices = computed(() => {
  return viewportStateStore.getSelectedImageIndices(generalViewerViewID.value);
});

const isCalculating = computed(() => ocgStore.isCalculating);
const calculatingProgress = computed(() => ocgStore.progress);
const calculationError = computed(() => ocgStore.error);

const recomputeCurves = async () => {
  isCurvesOpen.value = true;
  const targetViewer = generalViewerViewID.value;
  const result = await ocgStore.calculate(targetViewer);
  return result;
};

const updateCrossRef = () => {
  viewportStateStore.clearReferenceImageUids(generalViewerViewID.value);

  if (!currentImageIndices.value || currentImageIndices.value.seriesIdx === undefined)
    return;

  const sliceCount = currentStudyStore.getSliceCount(
    currentImageIndices.value.seriesIdx,
  );

  if (sliceCount <= 0) return;

  for (let i = 0; i < sliceCount; i++) {
    const imageId = currentStudyStore.getSliceImageUIDs(
      currentImageIndices.value.seriesIdx,
      i,
    )[0];
    viewportStateStore.addReferenceImage(
      generalViewerViewID.value,
      imageId,
      false,
    );
    if (i === currentImageIndices.value.sliceIdx) {
      viewportStateStore.setActiveReferenceImageUid(
        generalViewerViewID.value,
        imageId,
      );
    }
  }
};

onMounted(() => {
  updateCrossRef();
});

watchEffect(() => {
  if (
    userInteractActiveViewer.value
    || currentImageIndices.value
    || selfIndices.value
  ) {
    updateCrossRef();
  }
});
</script>

<template>
  <div class="column-container simple-panel">
    <div class="general-viewer-container">
      <VtkTwoView id="AnalysisGeneralView" title="Series Browser" />
    </div>

    <div class="ocg-panel-section">
      <OcgCurvesPanel
        v-model:is-open="isCurvesOpen"
        :viewer-i-d="activeViewerID"
        :is-calculating="isCalculating"
        :progress="calculatingProgress"
        :error="calculationError"
        @recompute="recomputeCurves"
      />
    </div>
    <div class="thumbnails-container">
      <SeriesThumbnailsOverlay layout="horizontal" />
    </div>
  </div>
</template>

<style scoped>
.column-container {
  display: flex;
  padding: 6px;
  flex-direction: column;
  border-radius: 10px;
  gap: 6px;
  height: 100%;
  max-height: 100%;
  overflow-y: auto;
  overflow-x: hidden;
}

.general-viewer-container {
  display: flex;
  justify-content: center;
  user-select: none;
  height: 280px;
  min-height: 240px;
  width: 400px;
  min-width: 400px;
  flex-shrink: 0;
}

.thumbnails-container {
  width: 100%;
  min-height: 160px;
  flex: 0 0 auto;
  display: flex;
  justify-content: center;
}

.ocg-panel-section {
  width: 100%;
  display: flex;
  flex-direction: column;
  flex: 0 0 auto;
  padding-bottom: 8px;
}
</style>
